import { useCallback, useRef, useState } from 'react';

const BOTTOM_TOLERANCE = 4;

const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    if (/auto|scroll/.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return null;
};

const useScrollShadows = () => {
  const [headerShadow, setHeaderShadow] = useState(false);
  const [footerShadow, setFooterShadow] = useState(false);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  const probeRef = useCallback((probe: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = undefined;
    if (!probe) {
      setHeaderShadow(false);
      setFooterShadow(false);
      return;
    }
    const root = getScrollParent(probe);
    if (!root) return;

    const update = () => {
      setHeaderShadow(root.scrollTop > 0);
      setFooterShadow(root.scrollTop + root.clientHeight < root.scrollHeight - BOTTOM_TOLERANCE);
    };

    update();
    root.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(root);
    Array.from(root.children).forEach((child) => observer.observe(child));
    if (probe.parentElement && probe.parentElement !== root) observer.observe(probe.parentElement);

    cleanupRef.current = () => {
      root.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  return { probeRef, headerShadow, footerShadow };
};

export default useScrollShadows;
